/**
 * ============================================================================
 * ESCROW REFUND COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Component for refunding escrow funds to buyer (Admin only)
 */

import React, { useState } from 'react';
import { useEscrow } from '../../hooks/useEscrow';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import { useAuthContext } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Textarea from '../common/Textarea';
import Modal from '../common/Modal';
import Input from '../common/Input';

/**
 * Escrow Refund Component
 * @param {Object} props
 * @param {string} props.escrowId - Escrow ID
 * @param {Object} props.escrow - Escrow object
 * @param {Function} props.onSuccess - Success callback
 * @returns {JSX.Element}
 */
const EscrowRefund = ({ escrowId, escrow, onSuccess }) => {
  const { refundEscrow, loading } = useEscrow(escrowId);
  const { showSuccess, showError } = useToast();
  const { formatCurrency, language } = useI18n();
  const { user } = useAuthContext();
  const [showModal, setShowModal] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundNotes, setRefundNotes] = useState('');

  // Only show for admins
  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return null;
  }

  if (!escrow || (escrow.status !== 'funded' && escrow.status !== 'frozen')) {
    return null;
  }

  const handleRefund = async () => {
    if (!refundReason.trim()) {
      showError(
        language === 'fr' ? 'La raison du remboursement est requise' : 'Refund reason is required'
      );
      return;
    }

    try {
      await refundEscrow(escrowId, {
        refund_reason: refundReason.trim(),
        refund_notes: refundNotes.trim() || undefined,
      });
      showSuccess(
        language === 'fr'
          ? 'Fonds remboursés avec succès!'
          : 'Funds refunded successfully!'
      );
      setShowModal(false);
      setRefundReason('');
      setRefundNotes('');
      if (onSuccess) onSuccess();
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec du remboursement' : 'Failed to refund funds')
      );
    }
  };

  return (
    <>
      <Card variant="outlined" className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl shadow-lg">
              ↩️
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-black text-gray-900 dark:text-white mb-1">
                {language === 'fr' ? 'Rembourser les fonds' : 'Refund Funds'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {language === 'fr'
                  ? 'Remboursez les fonds en escrow à l\'acheteur (Admin uniquement)'
                  : 'Refund escrow funds to buyer (Admin only)'}
              </p>
            </div>
          </div>
          <div className="bg-white/80 dark:bg-gray-800/80 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {language === 'fr' ? 'Montant à rembourser' : 'Amount to Refund'}:
              </span>
              <span className="text-xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {formatCurrency(escrow.amount || 0, escrow.currency || 'XAF')}
              </span>
            </div>
          </div>
          <Button
            variant="gradient"
            fullWidth
            size="lg"
            onClick={() => setShowModal(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 shadow-xl hover:shadow-2xl transition-all"
          >
            {language === 'fr' ? 'Rembourser les fonds' : 'Refund Funds'} →
          </Button>
        </div>
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setRefundReason('');
          setRefundNotes('');
        }}
        title={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-xl">
              ↩️
            </div>
            <span className="text-xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {language === 'fr' ? 'Rembourser les fonds' : 'Refund Funds'}
            </span>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Amount Display */}
          <Card variant="outlined" className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                {language === 'fr' ? 'Montant à rembourser' : 'Amount to Refund'}
              </p>
              <p className="text-3xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {formatCurrency(escrow.amount || 0, escrow.currency || 'XAF')}
              </p>
            </div>
          </Card>

          {/* Warning */}
          <Card variant="outlined" className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="font-bold text-red-800 dark:text-red-200 mb-1">
                  {language === 'fr' ? 'Action administrative' : 'Admin Action'}
                </p>
                <p className="text-sm text-red-700 dark:text-red-300">
                  {language === 'fr'
                    ? 'Cette action remboursera tous les fonds en escrow à l\'acheteur. Cette action est irréversible.'
                    : 'This action will refund all escrow funds to the buyer. This action is irreversible.'}
                </p>
              </div>
            </div>
          </Card>

          {/* Refund Reason */}
          <Textarea
            label={language === 'fr' ? 'Raison du remboursement (requis)' : 'Refund Reason (required)'}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder={
              language === 'fr'
                ? 'Expliquez pourquoi ce remboursement est nécessaire...'
                : 'Explain why this refund is necessary...'
            }
            rows={4}
            required
            error={
              !refundReason.trim()
                ? language === 'fr'
                  ? 'La raison est requise'
                  : 'Reason is required'
                : null
            }
          />

          {/* Refund Notes */}
          <Textarea
            label={language === 'fr' ? 'Notes de remboursement (optionnel)' : 'Refund Notes (optional)'}
            value={refundNotes}
            onChange={(e) => setRefundNotes(e.target.value)}
            placeholder={
              language === 'fr'
                ? 'Ajoutez des notes sur ce remboursement...'
                : 'Add notes about this refund...'
            }
            rows={3}
          />

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              fullWidth
              onClick={() => {
                setShowModal(false);
                setRefundReason('');
                setRefundNotes('');
              }}
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              variant="gradient"
              fullWidth
              onClick={handleRefund}
              loading={loading}
              disabled={!refundReason.trim()}
              className="bg-gradient-to-r from-purple-600 to-pink-600 shadow-xl hover:shadow-2xl transition-all"
            >
              {language === 'fr' ? 'Confirmer le remboursement' : 'Confirm Refund'} →
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EscrowRefund;
